import type { ColDef } from "ag-grid-community";
import { makeAutoObservable } from "mobx";
import type { TabGroupTab } from "@synthesis/shared-ui";
import { makeStoreProvider } from "@synthesis/shared-utils";

import { VersionExpandCell } from "./local-components/version-expand-cell";
import { VersionStatusCell } from "./local-components/version-status-cell";

export type ServiceVersionStatus = "ACTIVE" | "ARCHIVED";

export type ServiceApplyRoad = {
  id: string;
  name: string;
  code: string;
};

export type ServiceProcessVersion = {
  id: string;
  label: string;
  area: string;
  appliesTo: string;
  description: string;
  stagesCount: number;
  status: ServiceVersionStatus;
  /** Стадии процесса конкретной версии — показываются в раскрытой строке таблицы. */
  stages: ServiceProcessStep[];
  /** Дороги, на которых применяется версия — таблица в раскрытой строке. */
  roads: ServiceApplyRoad[];
};

export type ServiceProcessStep = {
  id: string;
  order: number;
  title: string;
  performer: string;
  /** Срок: «1 день», «4 дня» или признак финальной стадии. */
  duration: string;
  isFinal?: boolean;
};

// Бэкенда нет — версии процесса захардкожены по макету «Услуга - Выдача ТУ» / «Версии процесса».
export const MOCK_SERVICE_APPLY_ROADS: ServiceApplyRoad[] = [
  { id: "vsib", name: "Восточно-Сибирская", code: "VOSTOCHNO_SIBIRSKAYA" },
  { id: "msk", name: "Московская", code: "MOSKOVSKAYA" },
  { id: "gork", name: "Горьковская", code: "GORKOVSKAYA" },
  { id: "okt", name: "Октябрьская", code: "OKTYABRSKAYA" },
  { id: "dv", name: "Дальневосточная", code: "DALNEVOSTOCHNAYA" },
  { id: "priv", name: "Приволжская", code: "PRIVOLZHSKAYA" },
  { id: "zab", name: "Забайкальская", code: "ZABAYKALSKAYA" },
  { id: "sverd", name: "Свердловская", code: "SVERDLOVSKAYA" },
  { id: "sever", name: "Северная", code: "SEVERNAYA" },
];

export const MOCK_SERVICE_PROCESS_STEPS: ServiceProcessStep[] = [
  { id: "step-1", order: 1, title: "Входной контроль", performer: "Операторская служба", duration: "1 день" },
  { id: "step-2", order: 2, title: "В работе у аппарата НГ", performer: "Аппарат НГ", duration: "4 дня" },
  { id: "step-3", order: 3, title: "Формируется решение", performer: "Операторская служба", duration: "1 день" },
  { id: "step-4", order: 4, title: "Выдано ТУ", performer: "Операторская служба", duration: "Финальная стадия", isFinal: true },
];

export const MOCK_SERVICE_PROCESS_VERSIONS: ServiceProcessVersion[] = [
  {
    id: "v2.0",
    label: "v2.0, 18.04.26",
    area: "Общая",
    appliesTo: "8 дорог",
    description: "Упрощённая версия для Октябрьской дороги — без отдельного этапа службы пути.",
    stagesCount: 4,
    status: "ACTIVE",
    stages: MOCK_SERVICE_PROCESS_STEPS,
    roads: MOCK_SERVICE_APPLY_ROADS.filter((road) => road.code !== "SEVERNAYA"),
  },
  {
    id: "v1.5",
    label: "v1.5, 20.01.26",
    area: "Московская",
    appliesTo: "5 дорог",
    description: "Версия для Московской дороги — добавлено согласование с юридическим отделом перед выдачей ТУ.",
    stagesCount: 5,
    status: "ACTIVE",
    stages: MOCK_SERVICE_PROCESS_STEPS,
    roads: MOCK_SERVICE_APPLY_ROADS.filter((road) =>
      ["MOSKOVSKAYA", "GORKOVSKAYA", "OKTYABRSKAYA", "DALNEVOSTOCHNAYA", "PRIVOLZHSKAYA"].includes(road.code),
    ),
  },
  {
    id: "v1.0",
    label: "v1.0, 01.03.25",
    area: "Октябрьская",
    appliesTo: "6 дорог",
    description: "Общая версия маршрута выдачи ТУ, применяется на дорогах без отдельно назначенной версии.",
    stagesCount: 6,
    status: "ACTIVE",
    stages: MOCK_SERVICE_PROCESS_STEPS,
    roads: MOCK_SERVICE_APPLY_ROADS.filter((road) =>
      ["OKTYABRSKAYA", "VOSTOCHNO_SIBIRSKAYA", "DALNEVOSTOCHNAYA", "PRIVOLZHSKAYA", "ZABAYKALSKAYA", "SVERDLOVSKAYA"].includes(road.code),
    ),
  },
  {
    id: "v0.9",
    label: "v0.9, 11.02.23",
    area: "Общая",
    appliesTo: "9 дорог",
    description: "Устаревшая общая версия маршрута выдачи ТУ, заменена версией v1.0.",
    stagesCount: 6,
    status: "ARCHIVED",
    stages: MOCK_SERVICE_PROCESS_STEPS,
    roads: MOCK_SERVICE_APPLY_ROADS,
  },
];

export const SERVICE_DETAIL_TAB_KEYS = ["versions", "kits", "roads"] as const;
export type ServiceDetailTabKey = (typeof SERVICE_DETAIL_TAB_KEYS)[number];

// ---------------------------------- COLUMNS & TABS ----------------------------------

export const VERSION_TABLE_COLUMNS: ColDef<ServiceProcessVersion>[] = [
  {
    colId: "expand",
    headerName: "",
    width: 64,
    maxWidth: 64,
    pinned: "left",
    lockPosition: true,
    sortable: false,
    cellRenderer: VersionExpandCell,
  },
  { colId: "label", headerName: "Версия", field: "label", width: 150 },
  { colId: "area", headerName: "Область", field: "area", width: 130 },
  { colId: "applies", headerName: "Применяется", field: "appliesTo", width: 130 },
  { colId: "description", headerName: "Описание", field: "description", minWidth: 380, flex: 1 },
  { colId: "stages", headerName: "Стадии", field: "stagesCount", width: 100 },
  { colId: "status", headerName: "Статус", width: 150, cellRenderer: VersionStatusCell },
];

/** Колонки вкладки «Применение на дорогах»: дороги, на которых применяется услуга. */
export const SERVICE_ROAD_COLUMNS: ColDef<ServiceApplyRoad>[] = [
  { colId: "name", headerName: "Дорога", field: "name", minWidth: 280, flex: 1 },
  { colId: "code", headerName: "Код", field: "code", width: 220 },
];

export const DETAIL_TABS: TabGroupTab[] = [
  { key: "versions", value: "versions", title: "Версии процесса" },
  { key: "kits", value: "kits", title: "Комплект документов" },
  { key: "roads", value: "roads", title: "Применение на дорогах" },
];

// ---------------------------------- STORE ----------------------------------

export type ServiceDetailModalAction = { type: "register-version" };

class PageStore {
  public popupAction: ServiceDetailModalAction | null = null;
  public activeTab: string = "versions";

  constructor() {
    makeAutoObservable(this);
  }

  public setPopupAction(action: ServiceDetailModalAction | null) {
    this.popupAction = action;
  }

  public setActiveTab(tab: string) {
    this.activeTab = tab;
  }
}

const { StoreProvider: PageStoreProvider, useStore: usePageStore } = makeStoreProvider(PageStore);

export { PageStoreProvider, usePageStore };
